import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from "../config/app";
import { readStoredJson, writeStoredJson } from "../lib/storage";
import { translations } from "./translations";

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
  translateError: (code: string) => string;
  translateMessage: (code: string) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);
const LOCALE_STORAGE_KEY = "water-tracker-locale";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = readStoredJson<Locale>(LOCALE_STORAGE_KEY, DEFAULT_LOCALE);
    return SUPPORTED_LOCALES.includes(stored) ? stored : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    writeStoredJson(LOCALE_STORAGE_KEY, nextLocale);
    document.documentElement.lang = nextLocale;
  }, []);

  const t = useCallback(
    (key: string, variables?: Record<string, string | number>) => {
      const value = translations[locale].ui[key] ?? translations.en.ui[key] ?? key;
      return interpolate(value, variables);
    },
    [locale]
  );

  const translateError = useCallback(
    (code: string) => translations[locale].errors[code] ?? translations.en.errors[code] ?? translations[locale].errors.INTERNAL_ERROR,
    [locale]
  );

  const translateMessage = useCallback(
    (code: string) => translations[locale].messages[code] ?? translations.en.messages[code] ?? code,
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, translateError, translateMessage }),
    [locale, setLocale, t, translateError, translateMessage]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return value;
}

function interpolate(value: string, variables?: Record<string, string | number>): string {
  if (!variables) {
    return value;
  }
  return Object.entries(variables).reduce((current, [key, replacement]) => {
    return current.replaceAll(`{${key}}`, String(replacement));
  }, value);
}
